import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { MapPin, Building2, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const T = {
  heroTitle: { de: 'Über Uns', en: 'About Us' },
  heroSub: { de: 'Lernen Sie die Basic United Holding GmbH kennen – Ihre strategische Holding für innovative Unternehmenslösungen.', en: 'Get to know Basic United Holding GmbH – Your strategic holding for innovative business solutions.' },
  historyTitle: { de: 'Unsere Geschichte', en: 'Our History' },
  historyP1: { de: 'Die Basic United Holding GmbH wurde mit dem Ziel gegründet, eine starke Dachstruktur für spezialisierte Unternehmen zu schaffen. Als strategische Holding vereinen wir verschiedene Geschäftsfelder unter einem gemeinsamen Wertesystem.', en: 'Basic United Holding GmbH was founded with the goal of creating a strong umbrella structure for specialised companies. As a strategic holding, we unite various business fields under a shared value system.' },
  historyP2: { de: 'Unser Hauptsitz in Langenfeld (Rheinland) ist der zentrale Knotenpunkt für die Steuerung und Koordination unserer Tochtergesellschaften. Von hier aus entwickeln wir Strategien für nachhaltiges Wachstum und operative Exzellenz.', en: 'Our headquarters in Langenfeld (Rhineland) is the central hub for managing and coordinating our subsidiaries. From here, we develop strategies for sustainable growth and operational excellence.' },
  hq: { de: 'Hauptsitz', en: 'Headquarters' },
  atAGlance: { de: 'Basic United Holding auf einen Blick', en: 'Basic United Holding at a Glance' },
  legalForm: { de: 'Rechtsform', en: 'Legal Form' },
  location: { de: 'Sitz', en: 'Headquarters' },
  subs: { de: 'Tochtergesellschaften', en: 'Subsidiaries' },
  fields: { de: 'Geschäftsfelder', en: 'Business Fields' },
  fieldsVal: { de: 'Logistik, Beratung, Dienstleistung', en: 'Logistics, Consulting, Services' },
  structTitle: { de: 'Holding-Struktur', en: 'Holding Structure' },
  structSub: { de: 'Unsere Unternehmensstruktur auf einen Blick', en: 'Our corporate structure at a glance' },
  parent: { de: 'Muttergesellschaft', en: 'Parent Company' },
  logistics: { de: 'Logistik & Transport', en: 'Logistics & Transport' },
  consulting: { de: 'Unternehmensberatung', en: 'Business Consulting' },
  clearance: { de: 'Entrümpelung & Entsorgung', en: 'Clearance & Disposal' },
  mgmtTitle: { de: 'Geschäftsführung', en: 'Management' },
  mgmtSub: { de: 'Die Führung der Basic United Holding GmbH', en: 'The leadership of Basic United Holding GmbH' },
  mgmtRole: { de: 'Alleiniger Gesellschafter und Geschäftsführer', en: 'Sole Shareholder and Managing Director' },
};

export default function UeberUns() {
  const structureRef = useRef(null);
  const managementRef = useRef(null);
  const isStructureInView = useInView(structureRef, { once: true, margin: "-100px" });
  const isManagementInView = useInView(managementRef, { once: true, margin: "-100px" });
  const { language } = useLanguage();

  return (
    <main data-testid="ueber-uns-page" className="pt-20">
      <section className="bg-[#0F1A2E] py-24 md:py-32">
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="accent-line mb-6"></div>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">{T.heroTitle[language]}</h1>
            <p className="text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">{T.heroSub[language]}</p>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#0A1220]">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
              <div className="accent-line mb-6"></div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">{T.historyTitle[language]}</h2>
              <p className="text-lg text-white/70 leading-relaxed mb-6">{T.historyP1[language]}</p>
              <p className="text-lg text-white/70 leading-relaxed mb-6">{T.historyP2[language]}</p>
              <div className="flex items-start space-x-4 p-6 bg-[#0F1A2E]">
                <MapPin size={24} className="text-[#F5A623] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-white">{T.hq[language]}</p>
                  <p className="text-white/70">Schneiderstraße 63<br />40764 Langenfeld (Rheinland)</p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
              <div className="bg-[#0F1A2E] p-8 md:p-12 text-white">
                <h3 className="font-heading text-2xl font-semibold mb-6">{T.atAGlance[language]}</h3>
                <div className="space-y-6">
                  <div className="flex justify-between items-center py-4 border-b border-white/20"><span className="text-white/70">{T.legalForm[language]}</span><span className="font-semibold">GmbH</span></div>
                  <div className="flex justify-between items-center py-4 border-b border-white/20"><span className="text-white/70">{T.location[language]}</span><span className="font-semibold">Langenfeld</span></div>
                  <div className="flex justify-between items-center py-4 border-b border-white/20"><span className="text-white/70">{T.subs[language]}</span><span className="font-semibold">3</span></div>
                  <div className="flex justify-between items-center py-4"><span className="text-white/70">{T.fields[language]}</span><span className="font-semibold">{T.fieldsVal[language]}</span></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section ref={structureRef} className="section-spacing bg-[#0F1A2E]" data-testid="holding-structure-section">
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isStructureInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-16">
            <div className="flex justify-center mb-6"><div className="accent-line"></div></div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">{T.structTitle[language]}</h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">{T.structSub[language]}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={isStructureInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} className="max-w-4xl mx-auto">
            <div className="bg-[#0F1A2E] text-white p-8 text-center mb-8">
              <Building2 size={32} className="mx-auto mb-4" />
              <h3 className="font-heading text-xl md:text-2xl font-semibold mb-2">Basic United Holding GmbH</h3>
              <p className="text-white/70 text-sm uppercase tracking-wider">{T.parent[language]}</p>
            </div>
            <div className="flex justify-center mb-8"><div className="w-0.5 h-12 bg-[#0F1A2E]"></div></div>
            <div className="hidden md:block relative h-0.5 bg-[#0F1A2E] mx-8 mb-8">
              <div className="absolute left-1/6 top-0 w-0.5 h-8 bg-[#0F1A2E]"></div>
              <div className="absolute left-1/2 top-0 w-0.5 h-8 bg-[#0F1A2E] transform -translate-x-1/2"></div>
              <div className="absolute right-1/6 top-0 w-0.5 h-8 bg-[#0F1A2E]"></div>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { name: 'AtoB Logistic Solutions GmbH', cat: T.logistics },
                { name: 'DSB Business Solutions GmbH', cat: T.consulting },
                { name: 'AtoB Objektservice GmbH', cat: T.clearance },
              ].map((sub, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={isStructureInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }} className="bg-[#152238] p-6 rounded-xl border-l-4 border-[#F5A623]">
                  <h4 className="font-heading text-lg font-semibold text-white mb-2">{sub.name}</h4>
                  <p className="text-sm text-white/50">{sub.cat[language]}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section ref={managementRef} className="section-spacing bg-[#0A1220]" data-testid="management-section">
        <div className="container-main">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={isManagementInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="mb-16">
            <div className="accent-line mb-6"></div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">{T.mgmtTitle[language]}</h2>
            <p className="text-lg text-white/70 max-w-2xl">{T.mgmtSub[language]}</p>
          </motion.div>
          <div className="max-w-xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={isManagementInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 }} data-testid="management-card-0" className="bg-[#0F1A2E] p-8">
              <div className="w-16 h-16 bg-[#0F1A2E] flex items-center justify-center mb-6"><Users size={28} className="text-white" /></div>
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Admir Basic</h3>
              <p className="text-[#0F1A2E] font-medium">{T.mgmtRole[language]}</p>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
